// index.tsx — route table. One lazy-loaded chunk per page so the initial
// bundle stays small; feature modules code-split along their route groups.
import { lazyWithRetry } from './lazyWithRetry';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import RootLayout from './RootLayout';
import Login from './Login';
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';
import HomeRedirect from './HomeRedirect';
import { RequireCap } from '@/auth/RequireCap';

// Account pages
const Profile = lazyWithRetry(() => import('./Profile'));
const Account = lazyWithRetry(() => import('./Account'));

// Inventory (reference module) — each page is its own chunk.
const Dashboard = lazyWithRetry(() => import('@/features/inventory/pages/Dashboard'));
const Products = lazyWithRetry(() => import('@/features/inventory/pages/Products'));
const ProductDetail = lazyWithRetry(() => import('@/features/inventory/pages/ProductDetail'));
const Batches = lazyWithRetry(() => import('@/features/inventory/pages/Batches'));
const Locations = lazyWithRetry(() => import('@/features/inventory/pages/Locations'));
const Adjustments = lazyWithRetry(() => import('@/features/inventory/pages/Adjustments'));
const Reconciliation = lazyWithRetry(() => import('@/features/inventory/pages/Reconciliation'));
const Alerts = lazyWithRetry(() => import('@/features/inventory/pages/Alerts'));
const ImportExport = lazyWithRetry(() => import('@/features/inventory/pages/ImportExport'));

// Master Data — each page is its own chunk.
const MasterDataHome = lazyWithRetry(() => import('@/features/master-data/pages/MasterDataHome'));
const MdCatalog = lazyWithRetry(() => import('@/features/master-data/pages/Catalog'));
const MdBins = lazyWithRetry(() => import('@/features/master-data/pages/Bins'));
const MdStations = lazyWithRetry(() => import('@/features/master-data/pages/Stations'));
const MdDispenseLocations = lazyWithRetry(
  () => import('@/features/master-data/pages/DispenseLocations'),
);
const MdPolicies = lazyWithRetry(() => import('@/features/master-data/pages/Policies'));
const MdWorkflow = lazyWithRetry(() => import('@/features/master-data/pages/WorkflowConfig'));
const MdStores = lazyWithRetry(() => import('@/features/master-data/pages/Stores'));
const MdStoreTypes = lazyWithRetry(() => import('@/features/master-data/pages/StoreTypes'));
const MdBinTypes = lazyWithRetry(() => import('@/features/master-data/pages/BinTypes'));
const MdManufacturers = lazyWithRetry(() => import('@/features/master-data/pages/Manufacturers'));
const MdRbac = lazyWithRetry(() => import('@/features/master-data/pages/Rbac'));
const MdImportExport = lazyWithRetry(() => import('@/features/master-data/pages/ImportExport'));
const MdModuleSettings = lazyWithRetry(() => import('@/features/master-data/pages/ModuleSettings'));
const MdDevices = lazyWithRetry(() => import('@/features/master-data/pages/Devices'));
const MdLabels = lazyWithRetry(() => import('@/features/master-data/pages/Labels'));
const MdTrayMaster = lazyWithRetry(() => import('@/features/master-data/pages/TrayMaster'));

// Stocks — each page is its own chunk.
const StkDashboard = lazyWithRetry(() => import('@/features/stocks/pages/Dashboard'));
const DispensingDashboard = lazyWithRetry(
  () => import('@/features/stocks/pages/DispensingDashboard'),
);
const StkInward = lazyWithRetry(() => import('@/features/stocks/pages/Inward'));
const StkInwardV2 = lazyWithRetry(() => import('@/features/stocks/inward/InwardV2Page'));
const StkDispense = lazyWithRetry(() => import('@/features/stocks/pages/Dispense'));
const FlowBoard = lazyWithRetry(() => import('@/features/workflow/pages/Board'));
const FlowDetail = lazyWithRetry(() => import('@/features/workflow/pages/WorkflowDetail'));
const StkTrays = lazyWithRetry(() => import('@/features/stocks/pages/Trays'));
const StkTrayCapacity = lazyWithRetry(() => import('@/features/stocks/pages/TrayCapacity'));
const StkDispenseLocations = lazyWithRetry(
  () => import('@/features/stocks/pages/DispenseLocations'),
);
const StkBackorders = lazyWithRetry(() => import('@/features/stocks/pages/Backorders'));
const StkPartPacks = lazyWithRetry(() => import('@/features/stocks/pages/PartPacks'));
const StkTransfers = lazyWithRetry(() => import('@/features/stocks/pages/Transfers'));
const StkAdjustments = lazyWithRetry(() => import('@/features/stocks/pages/Adjustments'));
const StkImportScan = lazyWithRetry(() => import('@/features/stocks/pages/ImportScan'));
const StkReturns = lazyWithRetry(() => import('@/features/stocks/pages/Returns'));
const StkOtc = lazyWithRetry(() => import('@/features/stocks/pages/Otc'));
const StkReports = lazyWithRetry(() => import('@/features/stocks/pages/Reports'));

// Prescriptions — each page is its own chunk.
const RxDashboard = lazyWithRetry(() => import('@/features/prescriptions/pages/Dashboard'));
const RxDetail = lazyWithRetry(() => import('@/features/prescriptions/pages/Detail'));
const RxPatientSearch = lazyWithRetry(() => import('@/features/prescriptions/pages/PatientSearch'));
const RxManualEntry = lazyWithRetry(() => import('@/features/prescriptions/pages/ManualEntry'));

// Compliance — single page, own chunk.
const Compliance = lazyWithRetry(() => import('@/features/compliance/pages/Compliance'));

// Multi-Store — each page is its own chunk.
const MstDashboard = lazyWithRetry(() => import('@/features/multistore/pages/Dashboard'));
const MstNetworkStock = lazyWithRetry(() => import('@/features/multistore/pages/NetworkStock'));
const MstOverstock = lazyWithRetry(() => import('@/features/multistore/pages/Overstock'));
const MstRequests = lazyWithRetry(() => import('@/features/multistore/pages/Requests'));
const MstTracking = lazyWithRetry(() => import('@/features/multistore/pages/Tracking'));
const MstDiscrepancies = lazyWithRetry(() => import('@/features/multistore/pages/Discrepancies'));
const MstHistory = lazyWithRetry(() => import('@/features/multistore/pages/History'));

export const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  { path: '/forgot-password', element: <ForgotPassword /> },
  { path: '/reset/:token', element: <ResetPassword /> },
  {
    path: '/',
    element: <RootLayout />,
    children: [
      // Role-aware landing: pharmacists → /dispensing, everyone else → /inventory (audit #9).
      { index: true, element: <HomeRedirect /> },
      {
        path: 'inventory',
        children: [
          { index: true, element: <Dashboard /> },
          { path: 'products', element: <Products /> },
          { path: 'products/:id', element: <ProductDetail /> },
          { path: 'batches', element: <Batches /> },
          { path: 'batches/:id', element: <Batches /> },
          { path: 'locations', element: <Locations /> },
          { path: 'locations/:id', element: <Locations /> },
          { path: 'adjustments', element: <Adjustments /> },
          { path: 'reconciliation', element: <Reconciliation /> },
          { path: 'alerts', element: <Alerts /> },
          { path: 'import-export', element: <ImportExport /> },
        ],
      },
      {
        path: 'master-data',
        children: [
          {
            index: true,
            element: (
              <RequireCap cap="master.access">
                <MasterDataHome />
              </RequireCap>
            ),
          },
          {
            path: 'catalog',
            element: (
              <RequireCap cap="master.access">
                <MdCatalog />
              </RequireCap>
            ),
          },
          {
            path: 'bins',
            element: (
              <RequireCap cap="master.access">
                <MdBins />
              </RequireCap>
            ),
          },
          {
            path: 'dispense-locations',
            element: (
              <RequireCap cap="master.access">
                <MdDispenseLocations />
              </RequireCap>
            ),
          },
          {
            path: 'stations',
            element: (
              <RequireCap cap="master.access">
                <MdStations />
              </RequireCap>
            ),
          },
          {
            path: 'policies',
            element: (
              <RequireCap cap="master.policy">
                <MdPolicies />
              </RequireCap>
            ),
          },
          {
            path: 'stores',
            element: (
              <RequireCap cap="master.access">
                <MdStores />
              </RequireCap>
            ),
          },
          {
            path: 'devices',
            element: (
              <RequireCap cap="settings.edit">
                <MdDevices />
              </RequireCap>
            ),
          },
          {
            path: 'store-types',
            element: (
              <RequireCap cap="master.access">
                <MdStoreTypes />
              </RequireCap>
            ),
          },
          {
            path: 'bin-types',
            element: (
              <RequireCap cap="master.access">
                <MdBinTypes />
              </RequireCap>
            ),
          },
          {
            path: 'manufacturers',
            element: (
              <RequireCap cap="master.access">
                <MdManufacturers />
              </RequireCap>
            ),
          },
          {
            path: 'labels',
            element: (
              <RequireCap cap="master.access">
                <MdLabels />
              </RequireCap>
            ),
          },
          {
            path: 'tray-master',
            element: (
              <RequireCap cap="master.access">
                <MdTrayMaster />
              </RequireCap>
            ),
          },
          {
            path: 'rbac',
            element: (
              <RequireCap cap="settings.edit">
                <MdRbac />
              </RequireCap>
            ),
          },
          {
            path: 'import',
            element: (
              <RequireCap cap="import.run">
                <MdImportExport />
              </RequireCap>
            ),
          },
          {
            path: 'settings',
            element: (
              <RequireCap cap="settings.edit">
                <MdModuleSettings />
              </RequireCap>
            ),
          },
          {
            path: 'workflow',
            element: (
              <RequireCap cap="settings.edit">
                <MdWorkflow />
              </RequireCap>
            ),
          },
        ],
      },
      {
        path: 'stocks',
        children: [
          { index: true, element: <StkDashboard /> },
          { path: 'inward', element: <StkInwardV2 /> },
          { path: 'inward/:id', element: <StkInwardV2 /> },
          { path: 'inward-v2', element: <StkInwardV2 /> },
          { path: 'inward-v2/:id', element: <StkInwardV2 /> },
          // Moved to the Dispensing module — keep redirects so existing links resolve.
          { path: 'dispense', element: <Navigate to="/dispensing/queue" replace /> },
          { path: 'dispense/:id', element: <StkDispense /> },
          { path: 'trays', element: <Navigate to="/dispensing/trays" replace /> },
          { path: 'tray-capacity', element: <Navigate to="/dispensing/tray-capacity" replace /> },
          { path: 'dispense-locations', element: <Navigate to="/dispensing/locations" replace /> },
          { path: 'backorders', element: <Navigate to="/dispensing/backorders" replace /> },
          { path: 'partpacks', element: <Navigate to="/dispensing/partpacks" replace /> },
          { path: 'transfers', element: <StkTransfers /> },
          { path: 'adjustments', element: <StkAdjustments /> },
          { path: 'returns', element: <StkReturns /> },
          { path: 'otc', element: <StkOtc /> },
          { path: 'import', element: <StkImportScan /> },
          { path: 'reports', element: <StkReports /> },
        ],
      },
      // Unified workflow board + story detail — one pipeline across the whole lifecycle.
      { path: 'workflow', element: <FlowBoard /> },
      { path: 'workflow/:id', element: <FlowDetail /> },
      // Dispensing (stock outward) — its own module; each page is a normal route.
      { path: 'dispensing', element: <DispensingDashboard /> },
      { path: 'dispensing/queue', element: <StkDispense /> },
      { path: 'dispensing/queue/:id', element: <StkDispense /> },
      { path: 'dispensing/trays', element: <StkTrays /> },
      { path: 'dispensing/tray-capacity', element: <StkTrayCapacity /> },
      { path: 'dispensing/locations', element: <StkDispenseLocations /> },
      { path: 'dispensing/backorders', element: <StkBackorders /> },
      { path: 'dispensing/partpacks', element: <StkPartPacks /> },
      // Not-yet-ported modules (Remaining Port Checklist).
      {
        path: 'prescriptions',
        children: [
          { index: true, element: <RxDashboard /> },
          // Merged into the unified Workflow board — the prescription queue no longer stands alone.
          { path: 'queue', element: <Navigate to="/workflow" replace /> },
          { path: 'queue/:id', element: <RxDetail /> },
          { path: 'patients', element: <RxPatientSearch /> },
          { path: 'manual', element: <RxManualEntry /> },
          { path: 'ocr', element: <RxManualEntry /> },
        ],
      },
      {
        path: 'multistore',
        children: [
          { index: true, element: <MstDashboard /> },
          { path: 'stock', element: <MstNetworkStock /> },
          { path: 'overstock', element: <MstOverstock /> },
          { path: 'requests', element: <MstRequests /> },
          { path: 'tracking', element: <MstTracking /> },
          { path: 'discrepancies', element: <MstDiscrepancies /> },
          { path: 'history', element: <MstHistory /> },
        ],
      },
      {
        path: 'compliance',
        element: (
          <RequireCap cap="audit.read">
            <Compliance />
          </RequireCap>
        ),
      },
      // Account pages
      { path: 'profile', element: <Profile /> },
      { path: 'account', element: <Account /> },
      { path: '*', element: <Navigate to="/inventory" replace /> },
    ],
  },
]);
